
import {cc}                          from "@Common/react/callback-chain";
import {cs}                          from "@Common/react/chain-services";
import {consumeContext}              from "@Common/react/context";
import {Load2}                       from "@Common/react/load2";
import {scnv}                        from "@Common/react/state-change-nested-value";
import {UseState}                    from "@Common/react/use-state";
import {cnv, snv}                    from "@Common/utils/arr-path";
import {findMinE}                    from "@Common/utils/collections";
import {chain}                       from "@Common/utils/fs";
import {filterKeys, mapValues, omit} from "@Common/utils/objects";

import {AddressSubSection}      from "@Xpresso/routes/common/heavy/shipping-together-base/address-sub-section/address-sub-section";
import {ServiceLevelSubSection} from "@Xpresso/routes/common/heavy/shipping-together-base/service-level-sub-section/service-level-sub-section";
import {AddressData}            from "@Xpresso/routes/common/shipping/address-data";
import {formatServiceLevelName} from "@Xpresso/routes/customize/steps/checkout/shipping-section/common/service-level/format-service-level-name";

import {renderAfter} from "./expo-types";

export const CommonShippingModules = ({initEnrollment, templateOptions, agentId, branchId}) => [
    {
        name: "shippingAddress1",
        load: ({}, next) => cs(
            ["addressIdState", (_, next) => UseState({
                getInitValue: () => chain(
                    (
                        initEnrollment?.distribution_options?.shipping?.addressId ??
                        initEnrollment?.distribution_options?.mailing?.unaddressed_copies?.addressId
                    ),
                    (_) => ({
                        saved: _,
                        current: _,
                    })
                ),
                next,
            })],
            ["addressData", (_, next) => AddressData({agentId, branchId, next})],
            ["calAddressId", ({addressData, addressIdState}, next) => next(
                addressIdState.value.current ??
                (addressData.officeAddress.value ?? addressData.myAddresses.value?.find(v=>v))
                    ?.idPrimary
            )],

            ["addressSubSection", ({addressData, addressIdState, calAddressId}, next) => AddressSubSection({
                loading: addressData.loading,
                addressData,
                agentId,
                onChangeAddressId: (addressId) => scnv(addressIdState, ["current"], () => addressId),
                selectedAddressId: calAddressId,
                next,
            })],
            ({addressSubSection, addressData, addressIdState, calAddressId}) => {
                const address = [addressData.officeAddress.value, ...addressData.myAddresses.value || []].filter(v=>v).find((a) => a.idPrimary === calAddressId)
                return next({
                    addressId: calAddressId,
                    address,
                    renderAfter: addressSubSection.renderAfter,
                    // isBusinessAddress: calAddressId && addressData.officeAddress?.idPrimary === calAddressId,
                    enrollmentEditor: {
                        loading: addressData.loading,
                        $name: "shipping address",
                        valid: !addressSubSection.invalid,
                        changed: addressIdState.value.saved !== calAddressId,
                        prepareSave: (enrollment) => ({
                            newValue: chain(
                                enrollment,
                                (_) => snv(_, ["distribution_options", "shipping", "addressId"], calAddressId),
                                (_) => snv(_, ["distribution_options", "mailing", "unaddressed_copies", "addressId"], calAddressId),
                            ),
                            onSaved: () => scnv(addressIdState, ["saved"], () => calAddressId),
                        }),
                        errorsHandling: {
                            highlightErrors: () => {
                                addressSubSection.highlightErrors();
                            },
                        },
                    },
                    render: ({showErrors}) => addressSubSection.render({}),
                });
            },
        ),
        expo: [renderAfter],
    },
    {
        name: "shippingPackageWeight",
        load: ({shippedQuantity}, next) => {
            const weightPerQuantity = (
                templateOptions?.product?.idProduct === 8 ? 3/150 : 6/150
            );

            return next(shippedQuantity * weightPerQuantity);
        },
        requires: ["shippedQuantity"],
    },
    {
        name: "shippingServiceLevel1",
        load: ({shippingAddress1, shippingPackageWeight}, next) => cs(
            consumeContext("apis"),
            ["serviceLevelState", (_, next) => UseState({
                getInitValue: () => chain(
                    (
                        initEnrollment?.distribution_options?.shipping?.service_level ??
                        initEnrollment?.distribution_options?.mailing?.unaddressed_copies?.service_level
                    ),
                    (_) => ({
                        saved: _,
                        current: _,
                    })
                ),
                next,
            })],

            ["serviceLevels", ({apis}, next) => cs(

                ["serviceLevels", ({}, next) => Load2({
                    _key: JSON.stringify([
                        shippingAddress1.addressId,
                        shippingAddress1.address, // Because the address content can be changed (edit), and service levels will change (or change validation)
                        !!templateOptions,
                        shippingPackageWeight,
                    ]),
                    fetch: () => !shippingAddress1.addressId || !templateOptions ? null : apis.next.shipping.getShippingRates({
                        addressId: shippingAddress1.addressId,
                        agentId, branchId,
                        scFlow: true,
                        items: [
                            {
                                sku: templateOptions.sku,
                                selections: {distributionType: "shipping"},
                                "weight":shippingPackageWeight,
                            }
                        ],
                    }),
                    captureException: true,
                    next,
                })],
                ({serviceLevels}) => next(cnv(serviceLevels, ["value"], (_) => chain(
                    omit(_, ["SATURDAY_DELIVERY"]), // Never have SATURDAY_DELIVERY for Scheduled Campaigns
                    // We don't show estimated delivery as it's irrelevant here
                    (_) => mapValues(_, (v) => filterKeys(v, (k:string) => !k.startsWith("delivery_"))),
                ))),
            )],
            ["calServiceLevel", ({serviceLevelState, serviceLevels}, next) => next(cc(null,[
                (_, next) => serviceLevelState.value.current ?? next(),
                (_, next) => serviceLevels.loading ? null : next(),
                (_, next) => Object.keys(serviceLevels.value??{}).find((k) => k==="GROUND" || k === "GROUND_HOME") ?? next(),
                (_, next) => Object.keys(serviceLevels.value??{}).find((k) => k.endsWith("_GROUND") || k.endsWith("_GROUND_HOME")) ?? next(),
                (_,     ) => findMinE(Object.keys(serviceLevels.value??{}), (k) => serviceLevels.value[k].price),
            ]))],

            ["serviceLevelSubSection", (_, next) => ServiceLevelSubSection({next})],
            ({serviceLevelState, calServiceLevel, serviceLevels, serviceLevelSubSection}) => next({
                enrollmentEditor: {
                    loading: serviceLevels.loading,
                    $name: "shipping service level",
                    valid: calServiceLevel != null,
                    changed: serviceLevelState.value.saved !== calServiceLevel,
                    errorsHandling: {
                        highlightErrors: () => {
                            if (calServiceLevel == null) {
                                serviceLevelSubSection.highlightErrors();
                            }
                        },
                    },
                    prepareSave: (enrollment) => ({
                        newValue: chain(
                            enrollment,
                            (_) => snv( _, ["distribution_options", "shipping", "service_level"],calServiceLevel),
                            (_) => snv( _, ["distribution_options", "mailing", "unaddressed_copies", "service_level"],calServiceLevel),
                        ),
                        onSaved: () => scnv(serviceLevelState, ["saved"], () => calServiceLevel),
                    }),
                },
                serviceLevelLabel: calServiceLevel && formatServiceLevelName(calServiceLevel),
                serviceLevelPrice: calServiceLevel && serviceLevels.value?.[calServiceLevel]?.price,
                render: ({showErrors}) => serviceLevelSubSection.render({
                    apiError: serviceLevels.exception,
                    // shippingItemsCount: store.cart.items.filter((i) => i.selections.distributionType === "shipping").length,
                    selectedServiceLevel: calServiceLevel,
                    selectLevelDisclaimer: "The shipping rates shown are estimated and subject to change based on factors like package weight and delivery date. By choosing a service level, you acknowledge that the final shipping cost may be different. We'll always strive to provide the best rates.",
                    // estimatedDeliveryMessage: !isFreightServiceLevel(calServiceLevel) && (
                    //     store.cart?.shipping?.estimatedDeliveryMessage
                    // ),
                    getServiceLevels: () => serviceLevels.value,

                    onSelect: async (serviceLevel) => scnv(serviceLevelState, ["current"], () => serviceLevel),

                    shippingCharge: {
                        loading: serviceLevels.loading,
                        value: calServiceLevel && chain(
                            serviceLevels.value?.[calServiceLevel],
                            (_) => _?.price, // _.actual_price ?
                        ),
                    },
                    // errors: [
                    //     store.cart?.shipping?.weightSurcharge && {
                    //         name: "surChargeInfo",
                    //         message: "Shipping weight exceeds 10 pounds, a weight surcharge has been added.",
                    //     },
                    //     !store.cart?.shipping?.isServiceLevelAvailableForAllItems && {
                    //         name: "serviceLevelError",
                    //         message: "The selected service level is not available for all orders in the cart. Please choose a different service level or select \"Split Shipping\" to apply separate selections for your orders.",
                    //     },
                    // ].filter(v=>v),
                }),
            }),
        ),
        requires: ["shippingAddress1", "shippingPackageWeight"],
    },
];

